import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ViewInstituteDetailsModal = ({ isOpen, onClose, institute }) => {
  if (!isOpen || !institute) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-success/10 text-success',
      pending: 'bg-warning/10 text-warning',
      rejected: 'bg-error/10 text-error'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles?.[status] || 'bg-muted text-muted-foreground'}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
              <Icon name="Building2" size={24} color="var(--color-secondary)" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Institute Details</h2>
              <p className="text-sm text-muted-foreground">Complete institute information</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Institute Logo & Basic Info */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              {institute?.logo && (
                <div className="w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg bg-muted border border-border">
                  <Image
                    src={institute?.logo}
                    alt={institute?.logoAlt || `${institute?.name} logo`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-foreground mb-2">{institute?.name || 'N/A'}</h3>
                <div className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 mb-3 inline-block">
                  <p className="text-xs text-muted-foreground mb-1">Institute ID</p>
                  <p className="font-mono text-sm font-semibold text-primary break-all">{institute?.id || 'N/A'}</p>
                </div>
                <p className="text-muted-foreground mb-4">{institute?.description || 'No description available'}</p>
                <div className="flex flex-wrap gap-2">
                  {getStatusBadge(institute?.approval_status || institute?.approvalStatus)}
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary">
                    {institute?.type || 'Institute'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Phone" size={20} color="var(--color-primary)" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Email Address</p>
                <p className="font-medium text-foreground break-all">{institute?.email || institute?.user?.email || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Phone Number</p>
                <p className="font-medium text-foreground">{institute?.phone || institute?.contact_phone || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Contact Person</p>
                <p className="font-medium text-foreground">{institute?.contact_person || institute?.contactPerson || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Website</p>
                {institute?.website ? (
                  <a href={institute?.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline break-all">
                    {institute?.website}
                  </a>
                ) : (
                  <p className="font-medium text-foreground">N/A</p>
                )}
              </div>
            </div>
          </div>

          {/* Location Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="MapPin" size={20} color="var(--color-secondary)" />
              Location Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg md:col-span-2">
                <p className="text-sm text-muted-foreground mb-1">Full Address</p>
                <p className="font-medium text-foreground">{institute?.address || institute?.location || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">City</p>
                <p className="font-medium text-foreground">{institute?.city || institute?.user?.city || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">State</p>
                <p className="font-medium text-foreground">{institute?.state || institute?.user?.state || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Country</p>
                <p className="font-medium text-foreground">{institute?.country || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Postal Code</p>
                <p className="font-medium text-foreground">{institute?.postal_code || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Institute Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="GraduationCap" size={20} color="var(--color-accent)" />
              Institute Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Established Year</p>
                <p className="font-medium text-foreground">{institute?.established_year || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Accreditation</p>
                <p className="font-medium text-foreground">{institute?.accreditation || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Courses Offered</p>
                <p className="font-medium text-foreground">{institute?.courses_count || institute?.coursesCount || 0}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Booth Type</p>
                <p className="font-medium text-foreground capitalize">{institute?.booth_type || institute?.boothType || 'Standard'}</p>
              </div>
            </div>
          </div>

          {/* Application Details */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Calendar" size={20} color="var(--color-success)" />
              Application Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Application Date</p>
                <p className="font-medium text-foreground">{formatDate(institute?.created_at || institute?.appliedDate)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Last Updated</p>
                <p className="font-medium text-foreground">{formatDate(institute?.updated_at)}</p>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">User ID</p>
                <p className="font-mono text-xs text-foreground break-all">{institute?.user_id || institute?.user?.id || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewInstituteDetailsModal;